import React, { useCallback, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import CommandPalette from './CommandPalette';
import NotificationsBell from './NotificationsBell';

const PRIMARY_NAV = [
  { to: '/dashboard',        label: 'Dashboard' },
  { to: '/case-builder',     label: 'Case Builder' },
  { to: '/DenialResolve',    label: 'Denial Resolve' },
  { to: '/payers',           label: 'Payers' },
];

const OPERATE_DROPDOWN = [
  { to: '/intake',               label: 'Upload / Intake',          perm: 'intake.read' },
  { to: '/ClaimRight',           label: 'Claim Right · 837 scrub' },
  { to: '/CodingIntelligence',   label: 'Coding Intelligence · NCCI/MUE' },
  { to: '/MedicalNecessity',     label: 'Medical Necessity · LCD/NCD' },
  { to: '/AuthEligibility',      label: 'Auth & Eligibility · 270/271 + PA' },
  { to: '/denials/integrations', label: 'Data integrations',        perm: 'integration.write' },
];

const ANALYTICS_DROPDOWN = [
  { to: '/PayerIntelligence',          label: 'Payer Intelligence' },
  { to: '/analytics/contract-variance', label: 'Contract Variance' },
  { to: '/analytics/drg-hcc',           label: 'DRG / HCC review' },
];

const ADMIN_NAV = [
  { to: '/admin/users',   label: 'Users' },
  { to: '/admin/clients', label: 'Facilities' },
  { to: '/admin/roles',   label: 'Roles' },
  { to: '/admin/lookup',  label: 'Lookup Tables' },
  { to: '/admin/audit',   label: 'Audit Log' },
];

const HELP_DROPDOWN = [
  { to: '/test-drive',  label: 'Test Drive · sample cases' },
  { to: '/help/manual', label: 'User Manual' },
  { to: '/help/chat',   label: 'Chat' },
];

function initials(user) {
  const s = (user?.displayName || user?.email || '?').trim();
  return s.slice(0, 1).toUpperCase();
}

function inGroup(path, group) {
  return group.some((n) => path.toLowerCase() === n.to.toLowerCase());
}

export default function AppShell() {
  const { user, isAdmin, hasPermission, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const openPalette = useCallback(() => setPaletteOpen(true), []);
  const openChat = useCallback(() => navigate('/help/chat'), [navigate]);

  useEffect(() => {
    const onKey = (e) => {
      const isK = e.key === 'k' || e.key === 'K';
      if ((e.ctrlKey || e.metaKey) && isK) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const operateItems = OPERATE_DROPDOWN.filter((n) => !n.perm || hasPermission(n.perm));
  const isPolicyAdmin = (user?.email || '').toLowerCase() === 'hemalbpatel@cerebralmachines.ai';
  const adminItems = isPolicyAdmin
    ? [
        ...ADMIN_NAV,
        { to: '/admin/partners', label: 'Design Partners' },
        { to: '/admin/lcd-ncd', label: 'LCD/NCD' },
      ]
    : ADMIN_NAV;

  return (
    <div className="dm-shell">
      <header className="dm-topbar">
        <Link to="/dashboard" className="dm-brand" aria-label="RCMBoost by CerebralMachines.ai">
          <img
            src={`${process.env.PUBLIC_URL || ''}/rcmboost-logo-dark.png`}
            alt="RCMBoost — by CerebralMachines.ai"
            className="dm-brand__img"
          />
        </Link>

        <nav className="dm-nav">
          {PRIMARY_NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === '/dashboard' || n.to === '/DenialResolve'}
              className={({ isActive }) => `dm-nav__link${isActive ? ' is-active' : ''}`}
            >
              {n.label}
            </NavLink>
          ))}
          {operateItems.length > 0 && (
            <div className={`dm-nav__group${inGroup(location.pathname, operateItems) ? ' is-active' : ''}`}>
              <span className="dm-nav__link">Operate <span className="dm-nav__caret" aria-hidden="true">▾</span></span>
              <div className="dm-nav__dropdown">
                {operateItems.map((n) => (
                  <NavLink key={n.to} to={n.to} className="dm-nav__dropitem">
                    {n.label}
                  </NavLink>
                ))}
              </div>
            </div>
          )}
          <div className={`dm-nav__group${inGroup(location.pathname, ANALYTICS_DROPDOWN) ? ' is-active' : ''}`}>
            <span className="dm-nav__link">Analytics <span className="dm-nav__caret" aria-hidden="true">▾</span></span>
            <div className="dm-nav__dropdown">
              {ANALYTICS_DROPDOWN.map((n) => (
                <NavLink key={n.to} to={n.to} className="dm-nav__dropitem">
                  {n.label}
                </NavLink>
              ))}
            </div>
          </div>
          {isAdmin && (
            <div className={`dm-nav__group${location.pathname.startsWith('/admin') ? ' is-active' : ''}`}>
              <span className="dm-nav__link">Admin <span className="dm-nav__caret" aria-hidden="true">▾</span></span>
              <div className="dm-nav__dropdown">
                {adminItems.map((n) => (
                  <NavLink key={n.to} to={n.to} className="dm-nav__dropitem">
                    {n.label}
                  </NavLink>
                ))}
              </div>
            </div>
          )}
          <div className={`dm-nav__group${location.pathname.startsWith('/help') ? ' is-active' : ''}`}>
            <span className="dm-nav__link">Help <span className="dm-nav__caret" aria-hidden="true">▾</span></span>
            <div className="dm-nav__dropdown">
              {HELP_DROPDOWN.map((n) => (
                <NavLink key={n.to} to={n.to} className="dm-nav__dropitem">
                  {n.label}
                </NavLink>
              ))}
            </div>
          </div>
        </nav>

        <div className="dm-topbar__actions">
          <button
            type="button"
            className="dm-search-trigger"
            onClick={openPalette}
            aria-label="Open global search (Ctrl+K)"
            title="Search (Ctrl+K)"
          >
            <span className="dm-search-trigger__icon" aria-hidden="true">?</span>
            <span className="dm-search-trigger__hint">Search</span>
            <kbd className="dm-search-trigger__kbd">Ctrl K</kbd>
          </button>

          <button
            type="button"
            className="dm-chat-trigger"
            onClick={openChat}
            aria-label="Open chat"
            title="Chat"
          >
            <svg
              className="dm-chat-trigger__svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 15a2 2 0 0 1-2 2H8l-5 4V6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <span className="dm-chat-trigger__hint">Chat</span>
          </button>

          <NotificationsBell />

          <div className="dm-user">
            <button
              type="button"
              className="dm-user__btn"
              onClick={() => setMenuOpen((v) => !v)}
            >
              <span className="dm-user__avatar">{initials(user)}</span>
              <span className="dm-user__name">{user?.displayName || user?.email}</span>
            </button>
            {menuOpen && (
              <div className="dm-user__menu" onMouseLeave={() => setMenuOpen(false)}>
                <div className="dm-user__meta">
                  <div className="dm-user__email">{user?.email}</div>
                  <div className="dm-user__org">{user?.orgName || user?.orgId}</div>
                  <div className="dm-user__roles">{(user?.roles || []).join(', ') || 'no roles'}</div>
                </div>
                <Link to="/account" className="dm-user__item" onClick={() => setMenuOpen(false)}>
                  Account settings
                </Link>
                <Link to="/billing" className="dm-user__item" onClick={() => setMenuOpen(false)}>
                  Billing &amp; subscription
                </Link>
                <button type="button" className="dm-user__item dm-user__item--danger" onClick={logout}>
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="dm-main">
        <Outlet />
      </main>

      <footer className="dm-footer">
        <span className="dm-footer__copy">
          (c) {new Date().getFullYear()} CerebralMachines, Inc.
        </span>
        <Link to="/terms" className="dm-footer__link">Terms of Use</Link>
      </footer>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}
